import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { appEnv } from '../../../app.config';

export type TemplateChannel = 'email' | 'sms' | 'chat';

export interface MessagingTemplate {
  id: number;
  channel: TemplateChannel;
  name: string;
  folder: string | null;
  category: string | null;

  subject: string | null;

  body: string;
  starred: boolean;

  owner_name: string;
  owner_email: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateInput {
  channel: TemplateChannel;
  name: string;
  folder: string | null;
  category: string | null;
  subject: string | null;
  body: string;
}

@Injectable({
  providedIn: 'root'
})
export class MessagingTemplateService {
  constructor(private httpClient: HttpClient) {}

  list(channel: TemplateChannel): Observable<MessagingTemplate[]> {
    return this.httpClient
      .get<{ data: MessagingTemplate[] }>(`${appEnv.api}crm/messaging/templates`, { params: { channel } })
      .pipe(map((res) => res.data));
  }

  create(input: TemplateInput): Observable<MessagingTemplate> {
    return this.httpClient
      .post<{ data: MessagingTemplate }>(`${appEnv.api}crm/messaging/templates`, input)
      .pipe(map((res) => res.data));
  }

  update(id: number, input: Omit<TemplateInput, 'channel'>): Observable<MessagingTemplate> {
    return this.httpClient
      .put<{ data: MessagingTemplate }>(`${appEnv.api}crm/messaging/templates/${id}`, input)
      .pipe(map((res) => res.data));
  }

  /**
   * Move templates into a folder and/or reassign their category without
   * sending their bodies back (some run to hundreds of KB). `folder` and
   * `category` distinguish "set to empty" from "leave alone" by presence, so
   * only the keys actually being changed go on the wire.
   */
  bulkAssign(ids: number[], changes: { folder?: string | null; category?: string | null }): Observable<number> {
    return this.httpClient
      .post<{ data: { updated: number } }>(`${appEnv.api}crm/messaging/templates/bulk-assign`, { ids, ...changes })
      .pipe(map((res) => res.data.updated));
  }

  delete(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${appEnv.api}crm/messaging/templates/${id}`);
  }

  toggleStar(id: number): Observable<MessagingTemplate> {
    return this.httpClient
      .put<{ data: MessagingTemplate }>(`${appEnv.api}crm/messaging/templates/${id}/star`, {})
      .pipe(map((res) => res.data));
  }
}
